"use client";

import { LoginModal } from "@/components/LoginModal";
import { LoadingModal } from "@/components/core/LoadingModal";
import { trackEvent, trackLegacyEvent } from "@analytics/analytics";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { type LoginMethod, usePassportProvider } from "./PassportProvider";

interface LoginModalContextProps {
  openLoginModal: (callback?: () => Promise<void>) => void;
  isModalOpen: boolean;
}

const LoginModalContext = createContext<LoginModalContextProps | undefined>(
  undefined,
);

export const LoginModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loginCallback, setLoginCallback] = useState<
    (() => Promise<void>) | undefined
  >(undefined);
  const { t } = useTranslation();

  const openLoginModal = (callback?: () => Promise<void>) => {
    setIsModalOpen(true);
    setLoginCallback(() => callback);
    trackEvent({
      page: "Login",
      extras: {
        screen: "Login",
        userJourney: "SignIn",
        control: "SignInButton",
        controlLabel: "Sign in",
        interactionType: "click",
      },
    });
    trackLegacyEvent({
      screen: "Login",
      userJourney: "Profile",
      action: "Started",
    });
  };
  const closeLoginModal = useCallback(() => setIsModalOpen(false), []);
  const { loginDirectly, isLoggingIn, isLoggedIn } = usePassportProvider();

  const handleLoginMethodSelect = (method: LoginMethod) => {
    loginDirectly(method);
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        screen: "Login",
        userJourney: "SignIn",
        control: "SignInWith",
        controlLabel: `SignInWith${
          typeof method === "object" && "email" in method ? "Email" : method
        }`,
      },
    });
    closeLoginModal();
  };

  const afterLogin = useCallback(() => {
    if (typeof loginCallback === "function" && isLoggedIn) {
      loginCallback().finally(() => {
        setLoginCallback(undefined);
      });
    }
  }, [loginCallback, isLoggedIn]);

  useEffect(() => {
    if (!isLoggingIn) {
      afterLogin();
    }
  }, [isLoggingIn, afterLogin]);

  return (
    <LoginModalContext.Provider value={{ openLoginModal, isModalOpen }}>
      {children}
      <LoginModal
        isOpen={isModalOpen}
        onClose={closeLoginModal}
        onLoginMethodSelect={handleLoginMethodSelect}
      />
      <LoadingModal loading={isLoggingIn} text={t("logging_in")} />
    </LoginModalContext.Provider>
  );
};

export const useLoginModal = () => {
  const context = useContext(LoginModalContext);
  if (!context) {
    throw new Error("useLoginModal must be used within a LoginModalProvider");
  }
  return context;
};
